<template>
    <nav class="navigation bg-white shadow-sm relative z-50 sm:z-10">
        <div class="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
            <div class="flex justify-between h-16">
                <div class="flex">
                    <div class="flex-shrink-0 flex items-center mt-1">
                        <img
                            class="block"
                            src="@/assets/logo-full-sm.png"
                            alt="Upyield logo"
                            @click="$router.push({ name: 'Dashboard' })"
                        >
                    </div>
                    <div class="hidden sm:ml-6 sm:flex">
                        <router-link
                            :to="{ name: 'Dashboard' }"
                            active-class="border-primary-500 is-active"
                            class="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium leading-5 text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out"
                        >
                            Dashboard
                        </router-link>

                        <router-link
                            :to="{ name: 'Analytics' }"
                            active-class="border-primary-500 is-active"
                            class="ml-8 inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium leading-5 text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out"
                        >
                            Analytics
                        </router-link>

                        <router-link
                            :to="{ name: 'OfferWall' }"
                            active-class="border-primary-500 is-active"
                            class="ml-8 inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium leading-5 text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out"
                        >
                            Offer Wall
                        </router-link>

                        <router-link
                            :to="{ name: 'LiveOffers' }"
                            active-class="border-primary-500 is-active"
                            class="ml-8 inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium leading-5 text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out"
                        >
                            Pending & Live Offers
                        </router-link>

                        <router-link
                            :to="{ name: 'Postbacks' }"
                            active-class="border-primary-500 is-active"
                            class="ml-8 inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium leading-5 text-gray-500 hover:text-gray-700 hover:border-gray-300 transition duration-150 ease-in-out"
                        >
                            Postbacks
                        </router-link>
                    </div>
                </div>
                <div class="hidden sm:ml-6 sm:flex sm:items-center">
                    <!-- Profile dropdown -->
                    <div class="ml-3 relative">
                        <div>
                            <button
                                id="user-menu"
                                class="flex text-sm border-2 border-transparent rounded-full focus:outline-none focus:border-gray-300 transition duration-150 ease-in-out"
                                aria-label="User menu"
                                aria-haspopup="true"
                                @click="isProfileDropdownOpen.value = !isProfileDropdownOpen.value"
                            >
                                <UserIcon size="2x" />
                            </button>
                        </div>
                        <FadeAnimation
                            enter-class="duration-75"
                            leave-class="duration-75"
                        >
                            <div
                                v-if="isProfileDropdownOpen.value"
                                class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg z-10"
                            >
                                <div class="py-1 rounded-md bg-white shadow-xs">
                                    <a
                                        href="#"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                                        @click.prevent="$router.push({ name: 'ProfileDetails' })"
                                        @click="isProfileDropdownOpen.value = !isProfileDropdownOpen.value"
                                    >Profile</a>
                                    <div class="border-t border-gray-100" />
                                    <a
                                        href="#"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                                        @click.prevent="logout"
                                    >Sign out</a>
                                </div>
                            </div>
                        </FadeAnimation>
                    </div>
                </div>
                <div class="-mr-2 flex items-center sm:hidden">
                    <!-- Mobile menu button -->
                    <button
                        class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition duration-150 ease-in-out"
                        @click.prevent="isSideoverOpen.value = !isSideoverOpen.value"
                    >
                        <MenuIcon
                            size="1.5x"
                        />
                    </button>
                </div>
            </div>
        </div>

        <transition
            enter-active-class="ease-in-out duration-300"
            enter-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="ease-in-out duration-500"
            leave-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-show="isSideoverOpen.value"
                class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-10"
            />
        </transition>

        <transition
            enter-active-class="transform transition ease-in-out duration-500"
            enter-class="-translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transform transition ease-in-out duration-500"
            leave-class="translate-x-0"
            leave-to-class="-translate-x-full"
        >
            <div
                v-show="isSideoverOpen.value"
                class="fixed inset-0 overflow-hidden z-10"
            >
                <!-- Off-canvas menu for mobile -->
                <div class="md:hidden">
                    <div class="fixed inset-0 flex z-40">
                        <div
                            class="relative flex-1 flex flex-col max-w-xs w-full bg-primary-900"
                            @click.prevent="isSideoverOpen.value = !isSideoverOpen.value"
                        >
                            <div class="absolute top-0 right-0 -mr-12 p-1">
                                <button
                                    class="flex items-center justify-center h-12 w-12 rounded-full focus:outline-none focus:bg-gray-600"
                                    aria-label="Close sidebar"
                                >
                                    <svg
                                        class="h-6 w-6 text-white"
                                        stroke="currentColor"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                            <div class="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                                <nav class="mt-5 px-2 space-y-1 text-white">
                                    <router-link
                                        :to="{ name: 'Dashboard' }"
                                        active-class="text-white bg-primary-500 is-active"
                                        class="group flex items-center px-2 py-2 text-base leading-6 font-medium rounded-md hover:text-white hover:bg-primary-700 focus:outline-none focus:text-white focus:bg-primary-700 transition ease-in-out duration-150"
                                    >
                                        <HomeIcon class="mr-4 h-6 w-6 text-white group-hover:text-white group-focus:text-white transition ease-in-out duration-150" />
                                        Dashboard
                                    </router-link>

                                    <router-link
                                        :to="{ name: 'Analytics' }"
                                        active-class="text-white bg-primary-500 is-active"
                                        class="group flex items-center px-2 py-2 text-base leading-6 font-medium rounded-md hover:text-white hover:bg-primary-700 focus:outline-none focus:text-white focus:bg-primary-700 transition ease-in-out duration-150"
                                    >
                                        <ChartBarIcon class="mr-4 h-6 w-6 text-white group-hover:text-white group-focus:text-white transition ease-in-out duration-150" />
                                        Analytics
                                    </router-link>

                                    <router-link
                                        :to="{ name: 'OfferWall' }"
                                        active-class="text-white bg-primary-500 is-active"
                                        class="group flex items-center px-2 py-2 text-base leading-6 font-medium rounded-md hover:text-white hover:bg-primary-700 focus:outline-none focus:text-white focus:bg-primary-700 transition ease-in-out duration-150"
                                    >
                                        <NewspaperIcon class="mr-4 h-6 w-6 text-white group-hover:text-white group-focus:text-white transition ease-in-out duration-150" />
                                        Offer Wall
                                    </router-link>

                                    <router-link
                                        :to="{ name: 'LiveOffers' }"
                                        active-class="text-white bg-primary-500 is-active"
                                        class="group flex items-center px-2 py-2 text-base leading-6 font-medium rounded-md hover:text-white hover:bg-primary-700 focus:outline-none focus:text-white focus:bg-primary-700 transition ease-in-out duration-150"
                                    >
                                        <SpeakerphoneIcon class="mr-4 h-6 w-6 text-white group-hover:text-white group-focus:text-white transition ease-in-out duration-150" />
                                        Pending & Live Offers
                                    </router-link>

                                    <router-link
                                        :to="{ name: 'Postbacks' }"
                                        active-class="text-white bg-primary-500 is-active"
                                        class="group flex items-center px-2 py-2 text-base leading-6 font-medium rounded-md hover:text-white hover:bg-primary-700 focus:outline-none focus:text-white focus:bg-primary-700 transition ease-in-out duration-150"
                                    >
                                        <LinkIcon class="mr-4 h-6 w-6 text-white group-hover:text-white group-focus:text-white transition ease-in-out duration-150" />
                                        Postbacks
                                    </router-link>
                                </nav>
                            </div>
                            <div class="flex-shrink-0 flex border-t border-primary-700 p-4">
                                <a
                                    href="#"
                                    class="flex-shrink-0 group block focus:outline-none"
                                >
                                    <div class="flex items-center">
                                        <div class="ml-3">
                                            <p
                                                class="text-base leading-6 font-medium text-white"
                                                @click="$router.push({ name: 'ProfileDetails' })"
                                            >
                                                {{ user.value.user.email }}
                                            </p>
                                            <p
                                                class="text-sm leading-5 font-medium text-white group-hover:text-primary-100 group-focus:underline transition ease-in-out duration-150"
                                                @click="logout"
                                            >
                                                Sign out
                                            </p>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </div>
                        <div class="flex-shrink-0 w-14">
                            <!-- Force sidebar to shrink to fit close icon -->
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        <StatusBar v-if="user.value.shadow">
            Shadow login as <strong>{{ user.value.user.firstname }} {{ user.value.user.lastname }}</strong>
            by <strong>{{ user.value.shadow.firstname }} {{ user.value.shadow.lastname }}</strong>
        </StatusBar>
        <accept-t-cs-modal
            v-if="!user.value.shadow && user.value.partner?.terms_conditions?.acceptation"
            v-model="user.value.partner.terms_conditions.acceptation"
        />
    </nav>
</template>

<script>
import { MenuIcon, HomeIcon, ChartBarIcon, NewspaperIcon, SpeakerphoneIcon, LinkIcon, UserIcon } from '@vue-hero-icons/outline';
import FadeAnimation from '@/components/animation/FadeAnimation';
import { logout as authLogout } from '@/store/auth';
import router from '@/router';
import { defineComponent, ref } from '@vue/composition-api';
import StatusBar from '@/components/ui/StatusBar.vue';
import user from '@/store/user';
import AcceptTCsModal from '@/components/ui/AcceptTCsModal.vue';

export default defineComponent({
    name: 'AppNav',
    components: {
        AcceptTCsModal,
        StatusBar,
        MenuIcon,
        HomeIcon,
        ChartBarIcon,
        NewspaperIcon,
        SpeakerphoneIcon,
        LinkIcon,
        UserIcon,
        FadeAnimation,
    },
    setup () {
        const isSideoverOpen = ref(false);
        const isProfileDropdownOpen = ref(false);

        function logout () {
            authLogout()
                .then(() => router.push({ name: 'Login' }));
        }

        return {
            isSideoverOpen,
            isProfileDropdownOpen,
            logout,
            user,
        };
    },
});
</script>

<style lang="scss" scoped>
</style>
